import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'; // Emotional Journey
import ChangeHistoryIcon from '@mui/icons-material/ChangeHistory'; // Turning Point
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Resolution
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LooksOneIcon from '@mui/icons-material/LooksOne'; // Beginning
import PsychologyIcon from '@mui/icons-material/Psychology'; // Emotional Journey Title
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

const NarrativeArcAnalysisTranscript = ({ summaryData }) => {
  const [expandedArc, setExpandedArc] = useState(null);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedArc(isExpanded ? panel : false);
  };

  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Narrative Arc analysis data not available.
        </Box>
      </Card>
    );
  }

  const { results } = summaryData;

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Narrative Arc Analysis
          <AnalysisLensHelperText lensName="narrative_arc" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Overall Summary
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {results.summary || 'No summary provided.'}
          </Typography>
        </Box>

        {/* Arcs Accordions */}
        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 2 }}>
          Identified Narrative Arcs
        </Typography>

        {results.arcs?.length > 0 ? (
          results.arcs.map((arc, index) => {
            const panelId = `arc-panel-${index}`;
            const isExpanded = expandedArc === panelId;

            return (
              <Accordion
                key={panelId}
                expanded={isExpanded}
                onChange={handleAccordionChange(panelId)}
                disableGutters
                elevation={0}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1.5,
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': { margin: '0 0 12px 0' },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`${panelId}-content`}
                  id={`${panelId}-header`}
                  sx={{
                    bgcolor: isExpanded ? 'grey.50' : 'background.paper',
                    borderBottom: isExpanded ? 1 : 0,
                    borderColor: 'divider',
                    borderTopLeftRadius: 'inherit',
                    borderTopRightRadius: 'inherit',
                    minHeight: 64,
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 'medium', flexGrow: 1 }}>
                    {arc.arc_label}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Grid container spacing={3}>
                    {/* Beginning, Turning Point, Resolution */}
                    <Grid item xs={12}>
                      <Stack spacing={2}>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <LooksOneIcon fontSize="inherit" color="action" /> Beginning
                          </Typography>
                          <Typography variant="body2" sx={{ pl: 3 }}>
                            {arc.beginning}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <ChangeHistoryIcon fontSize="inherit" color="action" /> Turning Point
                          </Typography>
                          <Typography variant="body2" sx={{ pl: 3 }}>
                            {arc.turning_point}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            gutterBottom
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            <CheckCircleOutlineIcon fontSize="inherit" color="success" /> Resolution
                          </Typography>
                          <Typography variant="body2" sx={{ pl: 3 }}>
                            {arc.resolution}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    {/* Emotional Journey */}
                    {arc.emotional_journey?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <PsychologyIcon fontSize="inherit" color="action" /> Emotional Journey
                        </Typography>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            gap: 0.5,
                            pl: 3,
                          }}
                        >
                          {arc.emotional_journey
                            .flatMap((journeyStr) => journeyStr.split(/\s*→\s*/)) // Split if arrows are used
                            .map((emotion, idx, arr) => (
                              <React.Fragment key={idx}>
                                <Chip
                                  label={emotion.trim()}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                                {idx < arr.length - 1 && (
                                  <ArrowForwardIcon fontSize="small" color="disabled" />
                                )}
                              </React.Fragment>
                            ))}
                        </Box>
                      </Grid>
                    )}

                    {/* Quotes */}
                    {arc.quotes?.length > 0 && (
                      <Grid item xs={12}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                        >
                          <FormatQuoteIcon fontSize="inherit" color="action" /> Supporting Quotes
                        </Typography>
                        <List dense disablePadding sx={{ pl: 2 }}>
                          {arc.quotes.map((quote, idx) => (
                            <ListItem
                              key={idx}
                              disableGutters
                              sx={{ alignItems: 'flex-start', '&:not(:last-child)': { mb: 1 } }}
                            >
                              <ListItemIcon sx={{ minWidth: 28, mt: 0.5 }}>
                                <FormatQuoteIcon
                                  fontSize="small"
                                  sx={{ transform: 'scaleX(-1)' }}
                                  color="disabled"
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={quote}
                                primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Grid>
                    )}
                  </Grid>
                </AccordionDetails>
              </Accordion>
            );
          })
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            No specific narrative arcs identified in this analysis.
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default NarrativeArcAnalysisTranscript;
